import { TextArea } from "./types";
import { TextContent } from "./types";

interface ScaleGroupMember {
  area: {
    fontFamily: string;
    fontWeight?: string;
    fontSize: number;
    minFontSize?: number;
    maxWidth?: number;
    maxHeight?: number;
  };
  text: string;
}

interface ScaleGroupResult {
  fontSize: number;
  lines: string[];
}


export async function drawTemplateTexts(
  ctx: CanvasRenderingContext2D,
  textAreas: TextArea[],
  contents: TextContent
) {

  const computedBottomY = new Map<String, number>();

  //Gather Groups
  const groups = new Map<string, TextArea[]>();
  const ungrouped: TextArea[] = [];

  for (const area of textAreas) {
    if (!contents[area.id]) continue;
    if (area.scaleGroup) {
      if (!groups.has(area.scaleGroup)) groups.set(area.scaleGroup, []);
      groups.get(area.scaleGroup)!.push(area);
    } else {
      ungrouped.push(area);
    }
  }

  //calculate size for each group
  const precomputedResults = new Map<string, { fontSize: number; lines: string[] }>();

  for (const [, groupAreas] of groups) {
    // Fonts vorab laden
    for (const area of groupAreas) {
      await document.fonts.load(`${area.fontWeight ?? "normal"} ${area.fontSize}px ${area.fontFamily}`);
    }

    const members = groupAreas.map((area) => ({
      area,
      text: contents[area.id],
    }));

    const results = calculateSharedAutoFontSize(ctx, members);

    groupAreas.forEach((area, i) => {
      precomputedResults.set(area.id, results[i]);
    });
  }

  //draw texts
  for (const area of textAreas) {
    const text = contents[area.id];
    if (!text) continue; // if there is no content for this slot -> Skip

    let fontSize: number;
    let lines: string[];

    if (precomputedResults.has(area.id)) {
      // Wert aus der Gruppen-Berechnung übernehmen
      const result = precomputedResults.get(area.id)!;
      fontSize = result.fontSize;
      lines = result.lines;
    } else if (area.autoSize && area.maxWidth) {
      await document.fonts.load(`${area.fontWeight ?? "normal"} ${area.fontSize}px ${area.fontFamily}`);
      const result = calculateAutoFontSize(
        ctx, text, area.fontFamily, area.fontWeight,
        area.fontSize, area.minFontSize ?? 20, area.maxWidth, area.maxHeight
      );
      fontSize = result.fontSize;
      lines = result.lines;
    } else {
      const fontSpec = `${area.fontWeight ?? "normal"} ${area.fontSize}px ${area.fontFamily}`;
      await document.fonts.load(fontSpec);
      ctx.font = fontSpec;
      fontSize = area.fontSize;
      lines = area.maxWidth ? drawWrappedText(ctx, text, area.maxWidth) : [text];
    }

    const fontSpec = `${area.fontWeight ?? "normal"} ${fontSize}px ${area.fontFamily}`;
    ctx.font = fontSpec;
    ctx.fillStyle = area.color ?? "#000000";
    ctx.textAlign = area.textAlign ?? "left";
    ctx.textBaseline = "middle";

    const lineHeight = fontSize * 1;
    let startY = area.y;
    if (area.verticalOffsetFrom && computedBottomY.has(area.verticalOffsetFrom)) {
      startY = computedBottomY.get(area.verticalOffsetFrom)! + (area.gapAfterPrevious ?? 0);
    }

    let currentY = startY;
    for (const line of lines) {
      ctx.fillText(line, area.x, currentY);
      currentY += lineHeight;
    }
    computedBottomY.set(area.id, currentY - lineHeight + fontSize / 2);
  }
}

// Helper function, in case maxWidth enabled but autosize disabled
function drawWrappedTextFallback(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  return drawWrappedText(ctx, text, maxWidth);
}

// Einfacher Zeilenumbruch, falls maxWidth gesetzt ist
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) {
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);

  return lines;
}

function calculateAutoFontSize(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontFamily: string,
  fontWeight: string | undefined,
  maxFontSize: number,
  minFontSize: number,
  maxWidth: number,
  maxHeight?: number
): { fontSize: number; lines: string[] } {
  let fontSize = maxFontSize;

  while (fontSize >= minFontSize) {
    const fontSpec = `${fontWeight ?? "normal"} ${fontSize}px ${fontFamily}`;
    ctx.font = fontSpec;

    const lines = drawWrappedText(ctx, text, maxWidth);
    const lineHeight = fontSize * 1;
    const totalHeight = lines.length * lineHeight;

    const fitsWidth = lines.every((line) => ctx.measureText(line).width <= maxWidth);
    const fitsHeight = maxHeight ? totalHeight <= maxHeight : true;

    if (fitsWidth && fitsHeight) {
      return { fontSize, lines };
    }

    fontSize -= 1; 
  }

  const fontSpec = `${fontWeight ?? "normal"} ${minFontSize}px ${fontFamily}`;
  ctx.font = fontSpec;
  const lines = drawWrappedText(ctx, text, maxWidth);
  return { fontSize: minFontSize, lines };
}

export function calculateSharedAutoFontSize(
  ctx: CanvasRenderingContext2D,
  members: ScaleGroupMember[]
): ScaleGroupResult[] {
  // Skalierung startet bei 1.0 (= Basis-Schriftgrößen) und geht schrittweise runter
  let scale = 1.0;
  const step = 0.02;

  // Untere Grenze: sobald IRGENDEIN Feld seine minFontSize erreicht, abbrechen
  const minScale = Math.min(
    ...members.map((m) => (m.area.minFontSize ?? m.area.fontSize * 0.3) / m.area.fontSize)
  );

  while (scale >= minScale) {
    const results: ScaleGroupResult[] = [];
    let allFit = true;

    for (const member of members) {
      const scaledFontSize = Math.round(member.area.fontSize * scale);
      const fontSpec = `${member.area.fontWeight ?? "normal"} ${scaledFontSize}px ${member.area.fontFamily}`;
      ctx.font = fontSpec;

      const maxWidth = member.area.maxWidth ?? Infinity;
      const lines = drawWrappedText(ctx, member.text, maxWidth);
      const lineHeight = scaledFontSize * 1.05; // ggf. an deinen gewünschten Wert anpassen
      const totalHeight = lines.length * lineHeight;

      const fitsWidth = lines.every((line) => ctx.measureText(line).width <= maxWidth);
      const fitsHeight = member.area.maxHeight ? totalHeight <= member.area.maxHeight : true;

      if (!fitsWidth || !fitsHeight) {
        allFit = false;
      }

      results.push({ fontSize: scaledFontSize, lines });
    }

    if (allFit) {
      return results;
    }

    scale -= step;
  }

  // Fallback: kleinste erlaubte Skalierung nutzen, auch wenn's nicht perfekt passt
  return members.map((member) => {
    const scaledFontSize = Math.round(member.area.fontSize * minScale);
    const fontSpec = `${member.area.fontWeight ?? "normal"} ${scaledFontSize}px ${member.area.fontFamily}`;
    ctx.font = fontSpec;
    const lines = drawWrappedText(ctx, member.text, member.area.maxWidth ?? Infinity);
    return { fontSize: scaledFontSize, lines };
  });
}